import { useEffect, useRef, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  Plus,
  Save,
  Star,
  Trash2,
  Pencil,
  X,
  Upload,
  Clapperboard,
  MessageSquareQuote,
} from 'lucide-react';
import { PropertyReviewStatus } from '@pms/shared';
import type { HotelReviewDto, VideoStoryDto } from '@pms/shared';
import { usePlatformProperties } from '@/hooks/api/usePlatform';
import type { PlatformPropertyListItem } from '@/services/api/platform.api';
import {
  useHotelReviews,
  useCreateHotelReview,
  useUpdateHotelReview,
  useDeleteHotelReview,
  useVideoStories,
  useCreateVideoStory,
  useUpdateVideoStory,
  useDeleteVideoStory,
} from '@/hooks/api/useStorefrontContent';
import { usePresignUpload, type UploadKind } from '@/hooks/api/usePresignUpload';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { extractErrorMessage } from '@/lib/error';
import { cn } from '@/lib/utils';

const INPUT_CLS =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';
const LABEL_CLS = 'block text-xs font-semibold text-gray-500 mb-1';

// ── Shared atoms ──────────────────────────────────────────────────────────────

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
        active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
      )}
    >
      {active ? 'Live' : 'Hidden'}
    </span>
  );
}

/** Approved-only property picker (R1). Reuses the cached platform property list. */
function PropertyPicker({
  id,
  value,
  onChange,
  properties,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  properties: PlatformPropertyListItem[];
}) {
  const approved = properties.filter(
    (p) => p.reviewStatus === PropertyReviewStatus.APPROVED,
  );
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLS}>
        Property
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={INPUT_CLS}
      >
        <option value="">Select an approved property…</option>
        {approved.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} — {p.city}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Media URL field (TASK-62). Pasting an externally-hosted URL (CDN / video host /
 * any public image or video link) is the PRIMARY, always-working path — the
 * feature ships end-to-end with zero object storage. Direct file upload is an
 * OPTIONAL secondary affordance: it only appears while it can work and is hidden
 * the moment the presign endpoint reports no storage is configured (503). Either
 * way only a URL string is ever stored — no binary is kept in Postgres.
 */
function UploadField({
  fieldId,
  label,
  value,
  onChange,
  kind,
  accept,
}: {
  fieldId: string;
  label: string;
  value: string;
  onChange: (url: string) => void;
  kind: UploadKind;
  accept: string;
}) {
  const { state, start, reset } = usePresignUpload(kind);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (state.phase === 'done') onChange(state.publicUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const noKeys = state.phase === 'no-keys';

  return (
    <div className="space-y-1.5">
      {/* PRIMARY: paste an externally-hosted URL. */}
      <label htmlFor={fieldId} className={LABEL_CLS}>
        {label} URL
      </label>
      <input
        id={fieldId}
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://… (paste a hosted image/video URL)"
        className={INPUT_CLS}
      />

      {/* OPTIONAL: direct upload, only offered while storage is available. */}
      {!noKeys && !showUpload && (
        <button
          type="button"
          onClick={() => setShowUpload(true)}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
        >
          <Upload size={12} aria-hidden="true" /> Or upload a file (optional)
        </button>
      )}

      {!noKeys && showUpload && (
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            aria-label={`Upload ${label}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void start(file);
            }}
            className="block w-full text-xs text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-brand-700 hover:file:bg-brand-100"
          />
          <button
            type="button"
            aria-label="Cancel upload"
            onClick={() => {
              reset();
              setShowUpload(false);
              if (inputRef.current) inputRef.current.value = '';
            }}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {state.phase === 'requesting-url' && (
        <p className="flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 size={12} className="animate-spin" aria-hidden="true" /> Preparing upload…
        </p>
      )}
      {state.phase === 'uploading' && (
        <div
          role="progressbar"
          aria-valuenow={state.pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Upload progress"
          className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
        >
          <div
            className="h-full bg-brand-500 transition-all"
            style={{ width: `${state.pct}%` }}
          />
        </div>
      )}
      {state.phase === 'committing' && (
        <p className="flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 size={12} className="animate-spin" aria-hidden="true" /> Finishing…
        </p>
      )}
      {state.phase === 'error' && (
        <p className="text-xs text-red-600" role="alert">
          {state.message} — or just paste a hosted URL above.
        </p>
      )}
      {noKeys && (
        <p className="text-xs text-gray-500">
          Direct upload isn’t available — paste a hosted URL above.
        </p>
      )}
    </div>
  );
}

// ── Tab A — Hotel reviews ───────────────────────────────────────────────────────

const EMPTY_REVIEW = { propertyId: '', rating: 5, quote: '', author: '', location: '' };

function ReviewsTab({ properties }: { properties: PlatformPropertyListItem[] }) {
  const { data, isLoading, isError, refetch } = useHotelReviews();
  const create = useCreateHotelReview();
  const update = useUpdateHotelReview();
  const remove = useDeleteHotelReview();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_REVIEW });
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  function loadForEdit(r: HotelReviewDto) {
    setEditingId(r.id);
    setForm({
      propertyId: r.propertyId ?? '',
      rating: r.rating,
      quote: r.quote,
      author: r.author,
      location: r.location ?? '',
    });
    setError(null);
  }

  function resetForm() {
    setEditingId(null);
    setForm({ ...EMPTY_REVIEW });
    setError(null);
  }

  const invalid =
    !form.propertyId || !form.quote.trim() || !form.author.trim() || form.rating < 1;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (invalid) {
      setError('Property, rating, quote and author are all required.');
      return;
    }
    const body = {
      propertyId: form.propertyId,
      rating: form.rating,
      quote: form.quote.trim(),
      author: form.author.trim(),
      location: form.location.trim() || null,
    };
    const opts = {
      onSuccess: () => resetForm(),
      onError: (err: unknown) => setError(extractErrorMessage(err)),
    };
    if (editingId) update.mutate({ id: editingId, body }, opts);
    else create.mutate(body, opts);
  }

  const pending = create.isPending || update.isPending;

  return (
    <div className="space-y-5">
      <form
        onSubmit={handleSubmit}
        className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <PropertyPicker
            id="rv-property"
            value={form.propertyId}
            onChange={(v) => setForm((f) => ({ ...f, propertyId: v }))}
            properties={properties}
          />
          <div>
            <label htmlFor="rv-rating" className={LABEL_CLS}>
              Rating
            </label>
            <select
              id="rv-rating"
              value={form.rating}
              onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) }))}
              className={INPUT_CLS}
            >
              {[5, 4, 3, 2, 1].map((n) => (
                <option key={n} value={n}>
                  {n} star{n > 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="rv-author" className={LABEL_CLS}>
              Author
            </label>
            <input
              id="rv-author"
              type="text"
              value={form.author}
              onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label htmlFor="rv-location" className={LABEL_CLS}>
              Location (optional)
            </label>
            <input
              id="rv-location"
              type="text"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
        </div>
        <div>
          <label htmlFor="rv-quote" className={LABEL_CLS}>
            Quote
          </label>
          <textarea
            id="rv-quote"
            rows={2}
            value={form.quote}
            onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
            className={INPUT_CLS}
          />
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={pending || invalid}>
            {pending ? (
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            ) : editingId ? (
              <Save size={16} aria-hidden="true" />
            ) : (
              <Plus size={16} aria-hidden="true" />
            )}
            {editingId ? 'Save review' : 'Add review'}
          </Button>
          {editingId && (
            <Button type="button" variant="secondary" onClick={resetForm}>
              Cancel
            </Button>
          )}
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>
      </form>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <AlertCircle size={24} className="mx-auto mb-3 text-red-500" aria-hidden="true" />
            <p className="mb-4 text-sm text-red-700">Unable to load reviews.</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : (data ?? []).length === 0 ? (
          <EmptyState
            icon={MessageSquareQuote}
            title="No reviews yet"
            description="Add a curated hotel review using the form above."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Rating', 'Quote', 'Author', 'Property', 'Status', ''].map((c, i) => (
                    <th
                      key={c || `c-${i}`}
                      scope="col"
                      className="whitespace-nowrap px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data ?? []).map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <Stars rating={r.rating} />
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      <div className="line-clamp-2 max-w-md">{r.quote}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">{r.author}</td>
                    <td className="px-5 py-4 text-sm text-gray-500">
                      {r.propertyName ?? '—'}
                    </td>
                    <td className="px-5 py-4">
                      <ActiveBadge active={r.isActive} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          aria-label="Edit"
                          onClick={() => loadForEdit(r)}
                          className="rounded-md p-1 text-gray-400 hover:bg-brand-50 hover:text-brand-600"
                        >
                          <Pencil size={16} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          aria-label="Delete"
                          onClick={() => setConfirmId(r.id)}
                          className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmId !== null}
        title="Remove review"
        message="Remove this review from the storefront? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={() => {
          if (!confirmId) return;
          remove.mutate(confirmId, {
            onSettled: () => setConfirmId(null),
          });
        }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}

// ── Tab B — Video stories ───────────────────────────────────────────────────────

const EMPTY_STORY = {
  propertyId: '',
  name: '',
  role: '',
  quote: '',
  posterUrl: '',
  videoUrl: '',
};

function StoriesTab({ properties }: { properties: PlatformPropertyListItem[] }) {
  const { data, isLoading, isError, refetch } = useVideoStories();
  const create = useCreateVideoStory();
  const update = useUpdateVideoStory();
  const remove = useDeleteVideoStory();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_STORY });
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  function loadForEdit(s: VideoStoryDto) {
    setEditingId(s.id);
    setForm({
      propertyId: s.propertyId ?? '',
      name: s.name,
      role: s.role ?? '',
      quote: s.quote ?? '',
      posterUrl: s.posterUrl ?? '',
      videoUrl: s.videoUrl,
    });
    setError(null);
  }

  function resetForm() {
    setEditingId(null);
    setForm({ ...EMPTY_STORY });
    setError(null);
  }

  const invalid = !form.propertyId || !form.name.trim() || !form.videoUrl.trim();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (invalid) {
      setError('Property, name and a video URL are required.');
      return;
    }
    const body = {
      propertyId: form.propertyId,
      name: form.name.trim(),
      role: form.role.trim() || null,
      quote: form.quote.trim() || null,
      posterUrl: form.posterUrl.trim() || null,
      videoUrl: form.videoUrl.trim(),
    };
    const opts = {
      onSuccess: () => resetForm(),
      onError: (err: unknown) => setError(extractErrorMessage(err)),
    };
    if (editingId) update.mutate({ id: editingId, body }, opts);
    else create.mutate(body, opts);
  }

  const pending = create.isPending || update.isPending;

  return (
    <div className="space-y-5">
      <form
        onSubmit={handleSubmit}
        className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <PropertyPicker
            id="st-property"
            value={form.propertyId}
            onChange={(v) => setForm((f) => ({ ...f, propertyId: v }))}
            properties={properties}
          />
          <div>
            <label htmlFor="st-name" className={LABEL_CLS}>
              Name
            </label>
            <input
              id="st-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label htmlFor="st-role" className={LABEL_CLS}>
              Role (optional)
            </label>
            <input
              id="st-role"
              type="text"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <UploadField
            fieldId="st-poster"
            label="Poster (optional)"
            value={form.posterUrl}
            onChange={(url) => setForm((f) => ({ ...f, posterUrl: url }))}
            kind="image"
            accept="image/jpeg,image/png,image/webp"
          />
          <UploadField
            fieldId="st-video"
            label="Video"
            value={form.videoUrl}
            onChange={(url) => setForm((f) => ({ ...f, videoUrl: url }))}
            kind="video"
            accept="video/mp4,video/webm"
          />
          <div>
            <label htmlFor="st-quote" className={LABEL_CLS}>
              Quote (optional)
            </label>
            <textarea
              id="st-quote"
              rows={2}
              value={form.quote}
              onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={pending || invalid}>
            {pending ? (
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            ) : editingId ? (
              <Save size={16} aria-hidden="true" />
            ) : (
              <Plus size={16} aria-hidden="true" />
            )}
            {editingId ? 'Save story' : 'Add story'}
          </Button>
          {editingId && (
            <Button type="button" variant="secondary" onClick={resetForm}>
              Cancel
            </Button>
          )}
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>
      </form>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <AlertCircle size={24} className="mx-auto mb-3 text-red-500" aria-hidden="true" />
            <p className="mb-4 text-sm text-red-700">Unable to load stories.</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : (data ?? []).length === 0 ? (
          <EmptyState
            icon={Clapperboard}
            title="No stories yet"
            description="Add a hotelier video story using the form above."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Poster', 'Name', 'Role', 'Property', 'Status', ''].map((c, i) => (
                    <th
                      key={c || `c-${i}`}
                      scope="col"
                      className="whitespace-nowrap px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data ?? []).map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4">
                      {s.posterUrl ? (
                        <img
                          src={s.posterUrl}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="h-10 w-16 rounded object-cover"
                        />
                      ) : (
                        <span className="flex h-10 w-16 items-center justify-center rounded bg-gray-100 text-gray-300">
                          <Upload size={14} aria-hidden="true" />
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-gray-700">{s.name}</td>
                    <td className="px-5 py-4 text-sm text-gray-500">{s.role ?? '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-500">
                      {s.propertyName ?? '—'}
                    </td>
                    <td className="px-5 py-4">
                      <ActiveBadge active={s.isActive} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          aria-label="Edit"
                          onClick={() => loadForEdit(s)}
                          className="rounded-md p-1 text-gray-400 hover:bg-brand-50 hover:text-brand-600"
                        >
                          <Pencil size={16} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          aria-label="Delete"
                          onClick={() => setConfirmId(s.id)}
                          className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmId !== null}
        title="Remove story"
        message="Remove this story from the storefront? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={() => {
          if (!confirmId) return;
          remove.mutate(confirmId, { onSettled: () => setConfirmId(null) });
        }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────

const TABS = [
  { value: 'reviews' as const, label: 'Hotel reviews' },
  { value: 'stories' as const, label: 'Video stories' },
];

/**
 * Super-admin curation of storefront hotelier reviews + video stories (TASK-62).
 * Two tabs, each an add/edit form above a table of rows. Video stories use the
 * presigned-upload control (with a paste-URL fallback) for poster + video.
 */
export function AdminStorefrontStoriesPage() {
  const [tab, setTab] = useState<'reviews' | 'stories'>('reviews');
  const { data: properties } = usePlatformProperties();
  const props = properties ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Storefront Stories"
        description="Curate hotelier reviews and video stories shown on the public storefront."
      />

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Storefront stories">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              tab === t.value
                ? 'bg-brand-600 text-white'
                : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Both panels stay mounted so switching tabs never loses in-progress form state. */}
      <div role="tabpanel" className={tab === 'reviews' ? '' : 'hidden'}>
        <ReviewsTab properties={props} />
      </div>
      <div role="tabpanel" className={tab === 'stories' ? '' : 'hidden'}>
        <StoriesTab properties={props} />
      </div>
    </div>
  );
}
